// StageReleaseItemModal.jsx
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const StageReleaseItemModal = ({
  apiUrl,
  item,         // stage_equipment_items row (joined with product fields)
  maxQty,       // remaining qty allowed to release
  onClose,
  onReleased,
}) => {
  const today = new Date().toISOString().slice(0, 10);
  const [qty, setQty] = useState(Math.min(1, maxQty) || 1);
  const [date, setDate] = useState(today);
  const [releasedBy, setReleasedBy] = useState(''); // optional; backend defaults to session or 'System'
  const [destName, setDestName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const disabled = useMemo(() => !item || maxQty <= 0 || qty <= 0 || qty > maxQty, [item, maxQty, qty]);

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return;

    const payload = {
      stage_item_id: Number(item.id),
      released_quantity: Number(qty),
      release_date: date,
      // omit released_by to let backend fill from auth, or include if you want:
      ...(releasedBy.trim() ? { released_by: releasedBy.trim() } : {}),
      destination_name: destName.trim() || null,
      notes: notes.trim() || null,
    };

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${apiUrl}/inventory_crud.php`,
        { status: 'releaseStagingItem', data: payload },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const ok = (res.data && res.data.success === true)
        || (Array.isArray(res.data) && res.data[0] === 'success');

      if (ok) {
        toast.success('Release recorded');
        onReleased?.();
        onClose();
      } else {
        toast.error(res.data?.error || 'Failed to create release');
      }
    } catch (e2) {
      toast.error(e2?.response?.data?.error || 'API error creating release');
    } finally {
      setSubmitting(false);
    }
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-xl rounded-lg shadow-lg">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Release From Staging</h2>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="text-sm bg-gray-50 border rounded p-3">
            <div><span className="text-gray-500">Item:</span> <strong>{item.device || `#${item.device_id}`}</strong></div>
            <div className="text-gray-500">{item.description}</div>
            <div className="mt-1 text-gray-600">Staged Qty: <b>{item.quantity}</b></div>
            <div className="text-gray-600">Already Released: <b>{item._released || 0}</b></div>
            <div className="text-gray-900">Remaining: <b>{maxQty}</b></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Quantity</label>
              <input
                type="number"
                min={1}
                max={maxQty}
                value={qty}
                onChange={(e) => setQty(Number(e.target.value || 0))}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Release Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Released By (optional)</label>
              <input
                value={releasedBy}
                onChange={(e) => setReleasedBy(e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="Leave blank to use logged-in user"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Destination Name (optional)</label>
              <input
                value={destName}
                onChange={(e) => setDestName(e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="e.g., Jobsite A"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="Optional notes…"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" className="px-4 py-2 rounded border" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded disabled:opacity-60"
              disabled={disabled || submitting}
            >
              {submitting ? 'Releasing…' : 'Release'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StageReleaseItemModal;
